import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAppStore } from '@src/app/context'
import { logError } from '@src/lib/log'
import { LoadingIndicator } from '@ui/LoadingIndicator'

import type UniversalLoginAppState from './UniversalLoginAppState'
import * as styles from './UniversalLoginAuthorize.css'

const UniversalLoginAuthorize = () => {
  const { service, toast } = useAppStore()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const handleUnsuccessfulRedirect = useCallback(() => {
    service.authV2
      .checkIfAuthenticated()
      .catch(logError)
      .finally(() => {
        navigate('/login')
      })
  }, [navigate, service.authV2])

  const handleRedirectCallback = useCallback(async () => {
    try {
      const result =
        await service.authV2.auth0Client?.handleRedirectCallback<UniversalLoginAppState>()

      if (result?.appState) {
        const inviteToken = result.appState.inviteToken
        const referralCode = result.appState.referralCode
        const couponCode = result.appState.couponCode
        const phoneNumber = result.appState.phoneNumber
        const action = result.appState.action

        if (inviteToken) {
          runInAction(() => {
            // eslint-disable-next-line react-compiler/react-compiler
            service.authV2.inviteToken = inviteToken
          })
        }

        if (referralCode) {
          runInAction(() => {
            service.authV2.referralCode = referralCode
          })
        }

        if (couponCode) {
          await service.billing.fetchCoupon(couponCode)
        }

        if (phoneNumber) {
          service.authV2.phoneNumber = phoneNumber
        }

        if (action) {
          const handlerByAction: Record<typeof action, () => void> = {
            login: () => service.analytics.workspace.login('password'),
            passwordless_login: () => service.analytics.workspace.login('passwordless'),
            google_login: () => service.analytics.workspace.login('google'),
            passwordless_signup: () =>
              service.analytics.workspace.signup('passwordless', !inviteToken),
            google_signup: () => service.analytics.workspace.signup('google'),
          }

          handlerByAction[action]()

          service.dataDog.trackCustomAction({
            type: 'universal-login-authentication',
            action: action,
          })
        }
      }

      service.authV2.sessionStartedOn = Date.now()

      const intent = result?.appState?.intent
      await service.authV2?.completeAuthentication()
      navigate(intent ?? '/')
    } catch (error) {
      logError(error)

      if (error instanceof Error) {
        service.dataDog.trackCustomAction({
          type: 'universal-login-error',
          error: error,
        })
      }

      if (service.authV2.isClockSkewError(error)) {
        service.authV2.showLogoutReason('machine-clock-skew')
        toast.showError('Session expired. Please try again.')
      } else {
        toast.showError('Something went wrong. Please try again.')
      }

      handleUnsuccessfulRedirect()
    }
  }, [
    service.authV2,
    service.dataDog,
    service.analytics.workspace,
    service.billing,
    navigate,
    handleUnsuccessfulRedirect,
    toast,
  ])

  useEffect(() => {
    const code = searchParams.get('code')
    const error = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')

    if (errorDescription || error) {
      toast.showError(errorDescription || error)
    }

    if (!code) {
      handleUnsuccessfulRedirect()
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    handleRedirectCallback()
  }, [
    searchParams,
    navigate,
    handleRedirectCallback,
    toast,
    service.authV2,
    handleUnsuccessfulRedirect,
  ])

  return (
    <div className={styles.root}>
      <LoadingIndicator size={30} variant="circular" />
    </div>
  )
}

export default observer(UniversalLoginAuthorize)
